import React from 'react';
import {
  Typography as MuiTypography,
  Box as MuiBox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Radio,
  RadioGroup,
  Checkbox,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { QuestionInputProps } from '../types/questionnaire';

/**
 * Shared Likert input component for questionnaires
 * Handles: question_likert
 */
export const LikertInput: React.FC<QuestionInputProps> = ({
  question,
  value,
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const answersOptions = question.answersOptions || [];
  const questions = question.questions || [];
  const allowMultipleResponses = question.allowMultipleResponses;

  if (!answersOptions.length || !questions.length) {
    return null;
  }

  const currentValue = Array.isArray(value) ? value : [];

  return (
    <MuiBox
      sx={{
        width: '100%',
        overflowX: 'visible',
        padding: '5px',
        background: disabled ? '#f5f5f5' : theme.palette.colors.white,
        border: `1px solid ${theme.palette.colors.gray200}`,
        opacity: disabled ? 0.8 : 1,
      }}
    >
      <Table
        sx={{
          borderCollapse: 'separate',
          background: theme.palette.colors.white,
          width: '100%',
          tableLayout: 'auto',
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                minWidth: '200px',
                position: 'sticky',
                left: 0,
                borderRight: '1px solid rgba(224, 224, 224, 1);',
                zIndex: 2,
              }}
            ></TableCell>
            {answersOptions.map((option, idx) => (
              <TableCell key={idx} align="center">
                <MuiTypography marginLeft={'9px'} variant="h5">
                  {option.title || option.label}
                </MuiTypography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {!allowMultipleResponses
            ? questions.map((q, qIdx) => (
                <RadioGroup
                  key={qIdx}
                  style={{ display: 'table-row' }}
                  value={
                    currentValue.find((ans: any) => ans.questionId === q.id)
                      ?.answerId || ''
                  }
                  onChange={
                    !disabled
                      ? (e) => {
                          const newValue = currentValue.filter(
                            (ans: any) => ans.questionId !== q.id
                          );
                          newValue.push({
                            questionId: q.id,
                            answerId: e.target.value,
                          });
                          onChange(newValue);
                        }
                      : undefined
                  }
                  sx={{
                    verticalAlign: 'middle',
                    '&:last-child td': { borderBottom: 0 },
                  }}
                >
                  <TableCell
                    sx={{
                      minWidth: '200px',
                      position: 'sticky',
                      left: 0,
                      borderRight: '1px solid rgba(224, 224, 224, 1);',
                      zIndex: 2,
                    }}
                  >
                    <MuiBox
                      style={{ display: 'flex', gap: 10, alignItems: 'center' }}
                    >
                      <MuiTypography variant="h5">{`${
                        qIdx + 1
                      }.`}</MuiTypography>
                      <MuiTypography>{q.text}</MuiTypography>
                    </MuiBox>
                  </TableCell>

                  {answersOptions.map((a, aIdx) => (
                    <TableCell key={aIdx} align="center">
                      <Radio value={a.id} disabled={disabled} />
                    </TableCell>
                  ))}
                </RadioGroup>
              ))
            : questions.map((q, qIdx) => (
                <MuiBox
                  key={qIdx}
                  style={{ display: 'table-row' }}
                  sx={{
                    verticalAlign: 'middle',
                    '&:last-child td': { borderBottom: 0 },
                  }}
                >
                  <TableCell
                    sx={{
                      minWidth: '200px',
                      position: 'sticky',
                      left: 0,
                      borderRight: '1px solid rgba(224, 224, 224, 1);',
                      zIndex: 2,
                    }}
                  >
                    <MuiBox
                      style={{ display: 'flex', gap: 10, alignItems: 'center' }}
                    >
                      <MuiTypography variant="h5">{`${
                        qIdx + 1
                      }.`}</MuiTypography>
                      <MuiTypography>{q.text}</MuiTypography>
                    </MuiBox>
                  </TableCell>

                  {answersOptions.map((a, aIdx) => (
                    <TableCell key={aIdx} align="center">
                      <Checkbox
                        checked={currentValue.some(
                          (ans: any) =>
                            ans.questionId === q.id && ans.answerId === a.id
                        )}
                        onChange={
                          !disabled
                            ? (_, checked) => {
                                let newValue = [...currentValue];
                                if (checked) {
                                  newValue.push({
                                    questionId: q.id,
                                    answerId: a.id,
                                  });
                                } else {
                                  newValue = newValue.filter(
                                    (ans: any) =>
                                      ans.questionId !== q.id ||
                                      ans.answerId !== a.id
                                  );
                                }
                                onChange(newValue);
                              }
                            : undefined
                        }
                        disabled={disabled}
                      />
                    </TableCell>
                  ))}
                </MuiBox>
              ))}
        </TableBody>
      </Table>
    </MuiBox>
  );
};
